import { useContext, useEffect } from "react";
import { StyledFormulario } from "./style";
import { IoMdClose } from "react-icons/io";
import { RegistrationButton } from "../../../../../styles/registrationButton";
import { CustomState } from "../../../../../Hooks/State.hook";
import { UsersRegistrationContext } from "../../../../../contexts/admin/UsersRegistration.context";
import { Option } from "../../../../../components/CustomSelect";
import api from "../../../../../services/api";
import { toast } from "react-toastify";
import Input from "../../../../../components/Input";
import { defaultInput100, defaultSelect100 } from "../../../../../components-variants/defaultInputs";
import Select from "../../../../../components/Select";
import cssVars from "../../../../../styles/cssVariables.vars";

interface FormProps {
    closeFunction: () => void;
}

interface RegisterUserForm {
    NOME: string;
    LOGIN: string;
    SENHA: string;
    EMAIL: string;
    NIVEL: null | "ATIVO" | "RECEP" | "AMBOS" | "ADMIN";
    SETOR: null | number;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const Formulario = ({ closeFunction }: FormProps) => {
    const { getAllOperators } = useContext(UsersRegistrationContext);
    const isSubmitting = CustomState(false);    
    const formState = CustomState<RegisterUserForm>({
        NOME: "", LOGIN: "", SENHA: "", EMAIL: "", NIVEL: null, SETOR: null
    });
    const sectorOptions = CustomState<Array<Option<number>>>([]);

    const levelOptions: Array<Option<"ATIVO" | "RECEP" | "AMBOS" | "ADMIN">> = [
        { name: "Atendente (Ambos)", value: "AMBOS" },
        { name: "Atendente (Ativo)", value: "ATIVO" },
        { name: "Atendente (Receptivo)", value: "RECEP" },
        { name: "Supervisor", value: "ADMIN" },
    ];

    useEffect(() => {
        const controller = new AbortController();
        
        const loadSectors = async () => {
            try {
                const response = await api.get<{ CODIGO: number, NOME: string }[]>('/sectors', {
                    signal: controller.signal
                });
                sectorOptions.set(response.data.map(s => ({ name: s.NOME, value: s.CODIGO })));
            } catch (error) {
                if (!controller.signal.aborted) {
                    console.error('Erro ao carregar setores:', error);
                    toast.error('Falha ao carregar setores');
                }
            }
        };

        loadSectors();
        return () => controller.abort();
    }, []);


    const validateEmail = (email: string) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        
        if (!formState.value.NIVEL) {
            toast.error('Selecione o nível de acesso');
            return;
        }
        
        if (!formState.value.SETOR) {
            toast.error('Selecione o setor de atuação');
            return;
        }
        
        if (!validateEmail(formState.value.EMAIL)) {
            toast.error('Formato de e-mail inválido');
            return;
        }

        isSubmitting.set(true);
        
        try {
            await api.post('/operators', formState.value);
            await getAllOperators();
            toast.success('Atendente cadastrado com sucesso!');
        } catch (error) {
            console.error('Erro no cadastro:', error);
            toast.error('Erro ao cadastrar atendente');
        } finally {
            isSubmitting.set(false);
        }
        closeFunction();

    };

    const handleInputChange = (field: keyof RegisterUserForm) => 
        (e: React.ChangeEvent<HTMLInputElement>) => {
            formState.set(prev => ({ ...prev, [field]: e.target.value }));
        };

    return (
        <StyledFormulario onSubmit={handleSubmit}>
            <header>
                <h1>Cadastrar novo atendente</h1>
                <button
                    type="button"
                    onClick={closeFunction}
                    className="closeModal"
                    aria-label="Fechar modal"
                >
                    <IoMdClose />
                </button>
            </header>

            <Input
                placeholder="Login"
                minLength={6}
                onChange={handleInputChange("LOGIN")}
                value={formState.value.LOGIN}
                required
                {...defaultInput100}
                $padding={[0.5, 1]}
            />

            <Input
                placeholder="Nome completo"
                onChange={handleInputChange("NOME")}
                value={formState.value.NOME}
                required
                {...defaultInput100}
                $padding={[0.5, 1]}
            />

            <Input
                placeholder="Senha"
                type="password"
                minLength={6}
                onChange={handleInputChange("SENHA")}
                value={formState.value.SENHA}
                required
                {...defaultInput100}
                $padding={[0.5, 1]}
            />

            <Input
                placeholder="E-mail"
                type="email"
                onChange={(e) => {
                    const value = e.target.value.trim();
                    formState.set(prev => ({ ...prev, EMAIL: value }));
                }}
                value={formState.value.EMAIL}
                required
                {...defaultInput100}
                $padding={[0.5, 1]}
            />

            <Select
                onChange={(v) => formState.set(prev => ({ ...prev, NIVEL: v }))}
                options={levelOptions}
                placeholder="Nível de acesso"
                {...defaultSelect100}
                $padding={[0.5, 1]}
                $focusColor={cssVars.colorGrey[1]}
            />

            <Select
                onChange={(v) => formState.set(prev => ({ ...prev, SETOR: v }))}
                options={sectorOptions.value}
                placeholder="Setor de atuação"
                {...defaultSelect100}
                $padding={[0.5, 1]}
                $focusColor={cssVars.colorGrey[1]}
            />

            <RegistrationButton
                type="submit"
                disabled={isSubmitting.value}
                aria-busy={isSubmitting.value}
            >
                {isSubmitting.value ? 'Cadastrando...' : 'Cadastrar'}
            </RegistrationButton>
        </StyledFormulario>
    );
};